import React, { useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import "./App.css";
import "./style.scss";
import "bootstrap/dist/css/bootstrap.min.css";

import Navbar from "./Components/Navbar";
import usePageTracking from "./usePageTracking";
import LazyRender from "./Components/LazyRender";

// Eager-load: critical components
import Home from "./Pages/Home";
import NotFound from "./Pages/notFound";
import ValForm from "./Components/Form/ValForm";

// Lazy-load all others
const Mission = lazy(() => import("./Components/Mission"));
const AboutUs = lazy(() => import("./Components/aboutUs"));
const Business = lazy(() => import("./Pages/Business"));
const Geospatial = lazy(() => import("./Components/index/Geospatial"));
const Aml = lazy(() => import("./Components/index/Aml"));
const RetailData = lazy(
  () => import("./Components/index/Geospatial/RetailData"),
);
const RetailAnalytics = lazy(
  () => import("./Components/index/Geospatial/RetailAnalytics"),
);
const Admin = lazy(() => import("./admin/adminPage"));
const Login = lazy(() => import("./admin/LoginPage"));
const Register = lazy(() => import("./admin/RegisterPage"));
const ContactUs = lazy(() => import("./Components/index/ContactUs"));
const Banking = lazy(() => import("./Pages/Banking/Banking"));
const BTrial = lazy(() => import("./Pages/Banking_Trial/Banking"));
const Analytics = lazy(() => import("./Pages/Analytics/Analytics"));
const Feature = lazy(() => import("./Pages/Banking/Feature"));
const RiskComplianceSub = lazy(() => import("./Pages/RiskCompliance/Feature"));
// const Healthcare = lazy(() => import('./Pages/Healthcare/Healthcare'));
const NewHealthcare = lazy(() => import("./Pages/NewHealthcare/Healthcare"));
const Career = lazy(() => import("./Pages/Career/Career"));
const JobDescriptionPage = lazy(
  () => import("./Pages/Career/JobDescriptionPage"),
);
const CVS = lazy(() => import("./Pages/Cvs/CVS"));
const Academy = lazy(() => import("./Pages/Academy/Academy"));
const Logo = lazy(() => import("./Pages/TheLogo/TheLogo"));
const NewLogo = lazy(() => import("./Pages/TheLogo/NewLogo"));
const Gov = lazy(() => import("./Pages/Government/Gov"));
const Bi = lazy(() => import("./Pages/Bi/Bi"));
const AI = lazy(() => import("./Components/Ai"));
const DareToOvercome = lazy(() => import("./Components/DareToOvercome"));
const Clients = lazy(() => import("./Components/ClientsPage/ClientPage"));
const Multimedia = lazy(() => import("./Components/Multimedia"));
const MindMap = lazy(() => import("./Components/MindMap/MindMap"));
const CaseManagements = lazy(
  () => import("./Components/Geospatial/CaseManagements"),
);
const CaseGeospatial = lazy(
  () => import("./Components/Geospatial/CaseGeospatial"),
);
const CaseAnalytics = lazy(
  () => import("./Components/Geospatial/CaseAnalytics"),
);
const CaseBiometrics = lazy(
  () => import("./Components/Geospatial/CaseBiometrics"),
);
const CaseRiskAndThreatManagement = lazy(
  () => import("./Components/Geospatial/CaseRiskAndThreatManagement"),
);
const CaseKYC = lazy(() => import("./Components/Geospatial/CaseKYC"));
const EmployeeDetails = lazy(() => import("./Employees/Employees"));
const AnalyticsDisplay = lazy(
  () => import("./Pages/Analytics/AnalyticsPages/AnalyticsDisplay"),
);
const InProfit = lazy(() => import("./Pages/inProfit/Feature"));
const Marketing = lazy(() => import("./Pages/inMarketing/Feature"));
const InAnalytics = lazy(() => import("./Pages/inAnalytics/Feature"));
const InKernel = lazy(() => import("./Pages/inKernel/Feature"));
const InAsset = lazy(() => import("./Pages/inAsset/Feature"));
const GeospatialI = lazy(() => import("./Components/Geospatial/GeospatialI"));
const Digital = lazy(() => import("./Components/Digital/Digital"));
const Insurance = lazy(() => import("./Components/Insurance/insurance"));
const Overview = lazy(() => import("./Components/Insurance/Overview"));
const InDataA = lazy(() => import("./Pages/inData/Feature"));
const Consultancy = lazy(() => import("./ValooresNew/views/Consultancy"));
const NVtech = lazy(() => import("./Components/VTECH/NewVtech"));
const NewsEvents = lazy(() => import("./ValooresNew/views/NewsEvents"));
// const CEO = lazy(() => import("./ValooresNew/views/Ceo"));
const NewCEO = lazy(() => import("./ValooresNew/LetterOfCeo/LetterOfCeo"));
const EuroArabSummit2026 = lazy(() => import("./ValooresNew/LetterOfCeo/ParisConference"));
const PressRelease = lazy(() => import("./Pages/PressRelease/PressRelease"));
const ServicesGroup = lazy(
  () => import("./Components/servicesGroup/ServicesGroup"),
);
const NewVFDS = lazy(() => import("./Components/servicesGroup/NewVFDS"));
const Fatf = lazy(() => import("./Pages/Fatf/Fatf"));
const BigData = lazy(() => import("./Pages/BigData/BigData"));
// const Vcms = lazy(() => import('./Pages/Vcms/Vcms'));
const NewVcms = lazy(() => import("./Pages/NewVcms/Vcms"));
const V21 = lazy(() => import("./Components/V21/V21"));
const TermsAndConditions = lazy(
  () => import("./Components/TermsAndConditions"),
);
const PrivacyPolicy = lazy(() => import("./Components/PrivacyPolicy"));
const TelecommunicationTest = lazy(
  () => import("./Components/Telecommunication/NewTelecommunication"),
);
// const TelecommunicationTest123 = lazy(() => import('./Components/Telecommunication/TelecommunicatonTest'));
const VCIS = lazy(() => import("./Components/VCIS-page/VCIS"));
const VCIS1 = lazy(() => import("./Components/VCIS/VCIS-DataCrowd"));
const Monitor = lazy(() => import("./Components/VCIS/Components/Monitor"));
const Modern = lazy(
  () => import("./Components/VCIS/Components/modernApproaches"),
);
const Stages = lazy(() => import("./Components/VCIS/Components/stages"));
const WelcomeVCIS = lazy(
  () => import("./Components/VCIS/Components/welcomeVCIS"),
);
const AuditTrail = lazy(
  () => import("./Components/VCIS/Components/auditTrail"),
);
const Crime = lazy(() => import("./Components/VCIS/Components/crimeReduction"));
const Pipeline = lazy(() => import("./Components/VCIS/Components/pipeline"));
const Cctv = lazy(() => import("./Components/VCIS/Components/cctv"));
const GeospatialMeta = lazy(
  () => import("./Components/VCIS/Components/geospatial"),
);
const Technology = lazy(
  () => import("./Components/VCIS/Components/technology"),
);
const MediaY = lazy(() => import("./Components/index/MediaY"));
const IndexMediaDashboard = lazy(
  () => import("./admin/MediaDashboard/IndexMediaDashboard"),
);
const Biometric = lazy(() => import("./Components/VCIS/Components/biometrics"));
const Analytic = lazy(() => import("./Components/VCIS/Components/analytics"));
const MediaAdmin = lazy(() => import("./admin/MediaDashboard/MediaAdmin"));
// const ValForm = lazy(() => import('./Components/Form/ValForm'));

const MediaDocuments = lazy(
  () => import("./admin/MediaDashboard/MediaDocuments"),
);
const Potential = lazy(() => import("./Components/VCIS/Components/potential"));
const Consumer = lazy(() => import("./Components/VCIS/Components/consumer"));
const KycVcis = lazy(() => import("./Components/VCIS/Components/kycVcis"));
const FinancialVcis = lazy(
  () => import("./Components/VCIS/Components/financialCrime"),
);
const Prevention = lazy(
  () => import("./Components/VCIS/Components/prevention"),
);
const InternshipForm = lazy(
  () => import("./Components/InternshipForm/InternshipForm"),
);
const SecurePdfViewer = lazy(() => import("./Components/VCIS/SecurePdfViewer"));
const PdfCategory = lazy(() => import("./Components/VCIS/Pdf_Category"));
const Footer = lazy(() => import("./Components/Footer"));
const Contact = lazy(() => import("./Components/ContactUs/ContactUs"));
// const Documents = lazy(() => import('./Pages/documents/documents'));
const GroupingBy = lazy(() => import("./Pages/GroupingBySection/GroupingBy"));
const History = lazy(() => import("./Components/History/History"));
const RegAi = lazy(() => import("./Components/RegAi/RegAi"));
const NehmeProfile = lazy(
  () => import("./Components/NehmeProfile/NehmeProfile"),
);
// const VBSRegAi = lazy(() => import('./Components/VBS-REG-AI/VBSREGAI'));
const NewRegAi = lazy(() => import("./Components/NewRegAi/NewRegAi"));
const VALOORESArchitecture = lazy(
  () => import("./Components/VALOORESArchitecture/VALOORESArchitecture"),
);
const BlockChain = lazy(() => import("./Components/BlockChain/BlockChainPage"));
const VALOORESUAB = lazy(() => import("./Pages/Press_Release_ValooresUAB/ValooresUAB"));
const PolicyStatement = lazy(() => import("./Pages/Policy_Statement/Policy_Statement"));
// const VChat = lazy(() => import('./Components/VChat/VChat'));
// const Test = lazy(() => import('./Pages/NewInsideServices/NewInsideServices'));

ReactGA.initialize("G-BP198TJ5SM");

function App() {
  usePageTracking();
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);

  const shouldRenderNavbar =
    !location.pathname.includes("/admin") &&
    !location.pathname.includes("/Valoores103A") &&
    !location.pathname.includes("/MediaAdmin") &&
    !location.pathname.includes("/MediaDashboard") &&
    !location.pathname.includes("/ValForm") &&
    !location.pathname.includes("/InternshipForm");
  const shouldRenderFooter =
    !location.pathname.includes("/Valoores103A") &&
    !location.pathname.includes("/PdfViewer");
  const TITLE = "VALOORES";
  // const Ip='http://localhost/back/';
  // const Ip='http://192.168.3.15/back/';
  const Ip = "https://valoores.com/back/";

  useEffect(() => {
    const INACTIVITY_TIMEOUT = 60 * 60 * 1000;
    const handleInactivity = () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        window.location.href = "https://www.google.com";
      }, INACTIVITY_TIMEOUT);
    };
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) => window.addEventListener(event, handleInactivity));
    handleInactivity();
    return () => {
      clearTimeout(timeoutRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, handleInactivity),
      );
    };
  }, []);

  return (
    <Suspense fallback={<div className="lazy-fallback">Loading...</div>}>
      <>
        {!shouldRenderNavbar ? (
          <Routes>
            <Route path="/admin/*" element={<Admin Ip={Ip} />} />
            <Route path="/admin/Login" element={<Login Ip={Ip} />} />
            <Route path="/admin/Register" element={<Register Ip={Ip} />} />
            <Route path="/MediaAdmin" element={<MediaAdmin Ip={Ip} />} />
            <Route
              path="/MediaDashboard"
              element={<IndexMediaDashboard Ip={Ip} />}
            />
            <Route path="/ValForm" element={<ValForm Ip={Ip} />} />
            <Route
              path="/InternshipForm"
              element={<InternshipForm Ip={Ip} />}
            />
          </Routes>
        ) : (
          <>
            <Helmet>
              <title>{TITLE}</title>
            </Helmet>
            <Navbar Ip={Ip} />
            <Routes>
              <Route path="/" element={<Home Ip={Ip} />} />
              <Route path="/business" element={<Business />} />
              <Route path="/mission" element={<Mission />} />
              <Route path="/aboutUs/*" element={<AboutUs />} />
              <Route path="/Geospatial" element={<Geospatial />} />
              <Route path="/Aml" element={<Aml />} />
              <Route path="/News&Events" element={<NewsEvents />} />
              {/* <Route path="/LetterOfCeo" element={<CEO />} /> */}
              <Route path="/LetterOfCeo" element={<NewCEO />} />
              <Route path="/EuroArabSummit2026" element={<EuroArabSummit2026 />} />
              <Route
                path="/Telecommunication"
                element={<TelecommunicationTest />}
              />
              {/* <Route path="/TelecommunicationOld" element={<TelecommunicationTest123 />} /> */}

              <Route path="/Consultancy/" element={<Consultancy />} />
              <Route path="/Geospatial/retail-data" element={<RetailData />} />
              <Route
                path="/Geospatial/retail-analytics"
                element={<RetailAnalytics />}
              />
              <Route path="/Geospatial/cvs" element={<CVS />} />
              {/* <Route path="/aboutUs/leaders" element={<Leaders />} /> */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/ai" element={<AI />}></Route>
              <Route path="/banking" element={<Banking Ip={Ip} />}></Route>
              <Route path="/btrial" element={<BTrial />}></Route>
              <Route path="/analytics" element={<Analytics />}></Route>
              <Route path="/analytics/*" element={<AnalyticsDisplay />}></Route>
              <Route path="/banking/feature" element={<Feature />}></Route>
              <Route
                path="banking/riskandcompliance/*"
                element={<RiskComplianceSub />}
              ></Route>
              <Route path="banking/inProfit/*" element={<InProfit />}></Route>
              <Route path="banking/inDataA/*" element={<InDataA />}></Route>
              <Route
                path="banking/inMarketing/*"
                element={<Marketing />}
              ></Route>
              <Route
                path="banking/inAnalytics/*"
                element={<InAnalytics />}
              ></Route>
              <Route path="banking/inKernel/*" element={<InKernel />}></Route>
              <Route path="banking/inAsset/*" element={<InAsset />}></Route>
              <Route path="/hr/career" element={<Career />}></Route>
              <Route
                path="/hr/career/:jobtitle"
                element={<JobDescriptionPage />}
              ></Route>
              <Route
                path="/news/pressRelease"
                element={<PressRelease />}
              ></Route>
              <Route path="/hr/academy" element={<Academy />}></Route>
              <Route path="/clients" element={<Clients />}></Route>
              <Route path="/aboutUs/logo" element={<NewLogo />}></Route>
              {/* <Route path="/aboutUs/newlogo" element={<NewLogo />}></Route> */}
              <Route path="/solutions/gov" element={<Gov />}></Route>
              <Route
                path="/solutions/healthcare"
                element={<NewHealthcare />}
              ></Route>
              {/* <Route path="/solutions/newhealthcare" element={<NewHealthcare />}></Route> */}
              <Route
                path="/DareToOvercome"
                element={<DareToOvercome />}
              ></Route>
              <Route path="/MindMap" element={<MindMap />}></Route>
              <Route path="/Multimedia" element={<Multimedia />}></Route>
              <Route path="/*" element={<NotFound />}></Route>
              <Route path="/Bi" element={<Bi />}></Route>
              <Route
                path="/Geospatial-Analysis/Case-Managements"
                element={<CaseManagements />}
              ></Route>
              <Route
                path="/Geospatial-Analysis/Case-Geospatial"
                element={<CaseGeospatial />}
              ></Route>
              <Route
                path="/Geospatial-Analysis/Case-Analytics"
                element={<CaseAnalytics />}
              ></Route>
              <Route
                path="/Geospatial-Analysis/Case-Biometrics"
                element={<CaseBiometrics />}
              ></Route>
              <Route
                path="/Geospatial-Analysis/Case-RiskAndThreatManagement"
                element={<CaseRiskAndThreatManagement />}
              ></Route>
              <Route
                path="/Geospatial-Analysis/Case-KYC"
                element={<CaseKYC />}
              ></Route>
              <Route
                path="/Geospatial-Analysis"
                element={<Geospatial />}
              ></Route>
              <Route path="/GeospatialI" element={<GeospatialI />}></Route>
              <Route path="/Digital" element={<Digital />}></Route>
              <Route path="/Insurance" element={<Insurance />}></Route>
              <Route
                path="/Insurance/overview/*"
                element={<Overview />}
              ></Route>
              <Route path="/VFDS" element={<NewVFDS />}></Route>
              {/* <Route path="/VFDS" element={<ServicesGroup />}></Route> */}
              <Route path="/Fatf" element={<Fatf />}></Route>
              <Route path="/BigData" element={<BigData />}></Route>
              <Route path="/VCMS" element={<NewVcms Ip={Ip} />}></Route>
              {/* <Route path="/NewVCMS" element={<NewVcms />}></Route> */}
              <Route path="/V21" element={<V21 />}></Route>
              <Route
                path="/TermsAndCondition"
                element={<TermsAndConditions />}
              ></Route>
              <Route path="/Privacy" element={<PrivacyPolicy />}></Route>
              <Route
                path="/Employees/:employeeName"
                element={<EmployeeDetails Ip={Ip} />}
              />
              <Route path="/Vtech/" element={<NVtech Ip={Ip} />} />
              <Route path="/VCIS" element={<VCIS />} />
              <Route path="/Contact123" element={<Contact Ip={Ip} />} />
              <Route path="/History" element={<History Ip={Ip} />} />
              <Route path="/VBS-REG-AI" element={<RegAi Ip={Ip} />} />
              {/* <Route path="/VBS-REG-AI" element={<VBSRegAi Ip={Ip}/>} /> */}
              <Route path="/REG-AI" element={<NewRegAi Ip={Ip} />} />
              <Route
                path="/VALOORESArchitecture"
                element={<VALOORESArchitecture Ip={Ip} />}
              />
              <Route path="/BlockChain" element={<BlockChain Ip={Ip} />} />
              <Route
                path="/VIP/NehmeTaouk"
                element={<NehmeProfile Ip={Ip} />}
              />
              <Route path="/VALOORESUAB" element={<VALOORESUAB Ip={Ip} />} />
              <Route path="/PolicyStatement" element={<PolicyStatement Ip={Ip} />} />
              {/* <Route path="/Test123" element={<Test Ip={Ip}/>} /> */}

              {/*VCIS COMPONENTS  */}
              <Route path="/V-CIS" element={<VCIS1 Ip={Ip} />} />
              <Route path="/V-CIS/Pipeline" element={<Pipeline />} />
              <Route path="/V-CIS/cctv" element={<Cctv />} />
              <Route
                path="/V-CIS/geospatial-meta"
                element={<GeospatialMeta />}
              />
              <Route path="/V-CIS/technology" element={<Technology />} />
              <Route path="V-CIS/Crime" element={<Crime />} />
              <Route path="V-CIS/Monitor" element={<Monitor />} />
              <Route path="V-CIS/Modern" element={<Modern />} />
              <Route path="V-CIS/Stages" element={<Stages />} />
              <Route path="V-CIS/WelcomeVCIS" element={<WelcomeVCIS />} />
              <Route path="V-CIS/AuditTrail" element={<AuditTrail />} />
              <Route path="V-CIS/Potential" element={<Potential />} />
              <Route path="V-CIS/Biometric" element={<Biometric />} />
              <Route path="V-CIS/Analytic" element={<Analytic />} />
              <Route path="V-CIS/Consumer" element={<Consumer />} />
              <Route path="V-CIS/Kyc" element={<KycVcis />} />
              <Route path="V-CIS/FinancialCrime" element={<FinancialVcis />} />
              <Route path="V-CIS/Prevention" element={<Prevention />} />

              {/* Media Dashboard */}
              <Route path="/Media" element={<MediaY Ip={Ip} />} />
              <Route path="/V-CIS/documents" element={<GroupingBy Ip={Ip} />} />
              <Route
                path="/MediaDocument"
                element={<MediaDocuments Ip={Ip} />}
              />
              <Route
                path="/SecurePdfViewer"
                element={<SecurePdfViewer Ip={Ip} />}
              />
              <Route
                path="/ChoosedPdfCategory"
                element={<PdfCategory Ip={Ip} />}
              />

              {/* <Route path="/MediaPage" element={<MediaPage/>} />    */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <ContactUs Ip={Ip} />
            {/* <VChat Ip={Ip} /> */}
            <LazyRender>
              <Footer />
            </LazyRender>
          </>
        )}
      </>
    </Suspense>
  );
}

export default App;
